import type { MouseEvent, ReactNode } from 'react';
import { Button } from './Button';
import { IconButton } from './IconButton';
import { FormSubmit } from './Form';
import { OverflowMenu } from './OverflowMenu';
import type { OverflowMenuAction } from './OverflowMenu';
import { Grid } from './Layout';

// ------------------------------------------------------------------
// Semantic actions and governed action hosts — story 2-147r (P10 §2.7 row 2,
// "Action groups / placement"; DOD §4 G-22). Built over `2-147qa`'s converged
// Button/IconButton control-height contract.
// ------------------------------------------------------------------
//
// ── THE GAP ─────────────────────────────────────────────────────────────
// P10 §1.3's "Action hierarchy" section (story 2-137) already states what
// `primary`/`secondary`/`ghost`/`destructive` MEAN — but nothing enforced
// it. Every action cluster in the tree today (`TemplateCard`'s header row,
// `TemplatePublishBar`'s status row, `OrgAccessPage`'s PageHeader `actions`
// slot, `ReportDetailPage`'s toolbar) hand-rolls its own `<Button>` list —
// picking variant, size and `flex`/`gap` wrapping locally, one file at a
// time. Nothing stopped a second primary appearing in one region, or a
// row's actions wrapping instead of collapsing. The rule was advisory —
// written down, not enforced by the type a page author reaches for.
//
// This file makes it structural: a page/feature author supplies an
// `ActionDescriptor[]` — WHAT each action is (its label, handler, semantic
// variant, whether it's destructive) — and one of the host components below
// decides HOW that data renders: which control, what size, how it groups,
// and what collapses into an overflow menu once there are more actions than
// the host's own `maxVisible` budget. There is no path for a caller to hand
// a host raw JSX instead — see "NO REACTNODE ESCAPE HATCH" below. Six hosts
// (`HeaderActions`/`CardActions`/`RowActions`/`ToolbarActions`/`FormActions`/
// `DialogActions`) share this overflow/collapse mechanism; a seventh,
// `ChoiceGroup` (story 2-147xd, near the bottom of this file), covers a
// genuinely different rendered shape — an equal-width, always-visible
// toggle-button GROUP, never a place an overflow menu belongs — and is
// documented separately where it's defined.
//
// ── THE DESCRIPTOR VOCABULARY ──────────────────────────────────────────
// `ActionDescriptor` reuses P10 §1.3's own four-variant vocabulary
// (`primary`/`secondary`/`ghost`/`destructive`) verbatim — this story does
// not invent new terms, it makes the existing ones unavoidable. Two
// judgment calls made explicit here:
//
//   - `variant` defaults to `'secondary'`, not `'primary'`. `Button` itself
//     defaults to `'primary'` (the right default for a STANDALONE button),
//     but a host renders an ARRAY of actions together — defaulting every
//     unlabelled entry to `'primary'` would silently produce multiple
//     primary actions in one region, exactly what P10 §1.3's "a region
//     normally has ONE primary action" rule forbids. An author states
//     `'primary'` explicitly for the one action that earns it; every host
//     below composes whatever the caller supplies without second-guessing
//     it — enforcement of "at most one" is a lint/review concern (2-147xc),
//     not this file's job.
//   - `href` is deliberately NOT part of this vocabulary yet. P10 §1.3's
//     own action-hierarchy section already distinguishes a command
//     (`Button`) from a navigation (`<a>`/router `Link`) — but every real
//     proof-of-concept consumer this story wires up (see SESSION-HANDOFF)
//     is a COMMAND (a mutation, a dialog open, a client-side transition),
//     never a route change. Adding untested href-rendering (anchor vs.
//     button semantics, focus-ring parity, keyboard activation) for a
//     consumer that does not exist yet would be the same speculative-
//     abstraction mistake P10 §2.7 already declined for `KpiCard`/
//     `AddNewMenu` (rule of three, P18 §6) — left for the first host
//     consumer that genuinely needs it to add for real.
//
// `iconOnly` renders the action through `IconButton` (required accessible
// name, `label` doubling as `aria-label`) instead of `Button` — the
// RowActions host defaults compact/icon-only because a table/list row is
// the one real place P10 §2.7 already documents "often icon-only, often
// overflow into a menu when there are more than N".
//
// `pinned` is the one field every host's overflow mechanism reads (see
// below) — an author marks the action(s) that must NEVER collapse behind a
// menu (a destructive confirm, a form's own submit) as `pinned: true`.
//
// ── OVERFLOW / COLLAPSE MECHANISM (shared, all six hosts) ─────────────────
// Every host accepts a `maxVisible` budget (a fixed count — the same "named
// state set, not a live width measurement" discipline `StateMatrix`/
// `ResponsivePreview` already establish for this gallery; a real
// ResizeObserver-driven width measurement was considered and rejected as
// speculative complexity no real consumer here needs — the six
// proof-of-concept wirings below all have a small, KNOWN action count).
// `splitOverflow` (below) partitions `actions` into what renders inline and
// what collapses into a trailing `OverflowMenu`:
//   1. `pinned` actions always render inline, regardless of budget.
//   2. Remaining (`collapsible`) actions fill whatever budget is left after
//      pinned actions and the overflow trigger itself reserve their own
//      slot (`maxVisible - pinned.length - 1`), in the ORDER the caller
//      supplied them — i.e. an author's array order IS its priority order,
//      trailing/lowest-priority actions are what overflow first.
//   3. Anything left over renders as `OverflowMenu` items — reusing
//      `OverflowMenu` (story 2-137) rather than a second popover
//      mechanism, per this repo's own "rule of three"/no-near-duplicate-
//      primitive discipline (P18 §6).
// When `actions.length <= maxVisible`, nothing collapses and no
// `OverflowMenu` renders at all — the common case for every real
// proof-of-concept below.
//
// ── NO REACTNODE ESCAPE HATCH ──────────────────────────────────────────
// None of the six components below (or `ActionDescriptor` itself) accepts
// `children`, `extra`, or any other `ReactNode`-typed prop. Every rendered
// control is one this file itself chooses (`Button`, `IconButton`,
// `FormSubmit`, `OverflowMenu`) from data the caller supplies — a caller
// cannot smuggle a raw `<button>` or a bespoke variant/size past the host.
// If a real consumer ever needs something the descriptor vocabulary cannot
// express, the fix is to grow the vocabulary (an new `ActionDescriptor`
// field), never to add an escape prop — the exact discipline `2-147xc`
// (later in this chain) exists to keep true going forward.
//
// ── REGISTRY-MEMBERSHIP GAP (expected, non-blocking, precedented) ────────
// This file is new — `frontend/scripts/census/ui-registry.json` is DERIVED
// from `ui-census.json` (story 2-147m's frozen inventory, "not re-run by
// this story" per that generator's own header comment), so a genuinely new
// `components/ui` file cannot appear in it without re-running the census
// generator itself, out of this story's scope. `eslint`'s
// `design-language/registry-membership` rule accordingly warns once on this
// file — the SAME accepted, documented, non-blocking gap
// `components/gallery/GalleryPage.tsx`'s own header comment already
// recorded for its six new files (story 2-147p): "invisible to
// ui-registry.json... a real, named, non-blocking gap for whichever story
// next re-syncs the registry, not silently hidden." Recorded here, not
// worked around.

export type ActionVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';

export interface ActionDescriptor {
  /** Stable identity — React key, and what the overflow split keys its pin/collapse decision on. */
  id: string;
  /** Visible label (labelled actions) or accessible name (`iconOnly` actions). */
  label: string;
  /**
   * The command this action performs. Optional only so a `FormActionDescriptor`
   * with `type: 'submit'` (whose real submit handler lives on the enclosing
   * `<Form onSubmit>`, not on the button itself — see `FormActions` below) can
   * omit it; every other action needs a real handler or it does nothing.
   */
  /**
   * Story 2-147xd widened this from `() => void` (backward-compatible — a
   * handler that ignores the event still type-checks) to let a real
   * consumer read the native click event when it genuinely needs to
   * (`ReviewQueuePage.tsx`'s own table-row action, whose `stopPropagation()`
   * stops the surrounding row's own click-to-navigate from double-firing
   * — a real behavioral need, not a style choice, so this grows the
   * descriptor's own vocabulary rather than leaving that one control
   * unconverted).
   */
  onClick?: (event?: MouseEvent) => void;
  /** P10 §1.3's action-hierarchy vocabulary. Defaults to `'secondary'` — see this file's header comment for why not `'primary'`. */
  variant?: ActionVariant;
  /** Decorative icon — required when `iconOnly` is true. */
  icon?: ReactNode;
  /** Renders via `IconButton` (required accessible name) instead of `Button`. */
  iconOnly?: boolean;
  disabled?: boolean;
  /** Same contract as `Button`/`IconButton`'s own `loading` — spinner + `aria-busy` + implicit disable. */
  loading?: boolean;
  /**
   * Visible reason shown as the control's `title` when `disabled` — matching
   * the "explains disabled ones" convention this codebase already follows
   * (`TemplatePublishBar`, `InspectionLifecycleBar`). A `title`-only
   * affordance is not reachable by keyboard/touch on its own; a host does
   * not force a second visible text node, callers who need one still render
   * their own beside the host, same as those two existing files do.
   */
  disabledReason?: string;
  /** Never collapses into the overflow menu — see this file's header comment. */
  pinned?: boolean;
  /**
   * Story 2-147xd — a real, repeated (4 independent call sites: `ReviewAnswersPane.tsx`
   * "Flag", `RequestChangesDialog.tsx` "Remove", `ReviewQueuePage.tsx`/
   * `ReportsListPage.tsx` "Review"/"Open report") gap this file's own header
   * comment anticipated ("grow the vocabulary, never add an escape prop"):
   * a labelled (non-`iconOnly`) action whose VISIBLE text repeats identically
   * on every row of a list, so only a per-row DISTINCT accessible name
   * disambiguates it for assistive tech — `label` alone conflates "what the
   * button says" with "what a screen reader announces", which this field
   * splits apart without adding a raw ReactNode/className escape. Optional;
   * when omitted the control's accessible name stays `label`, unchanged for
   * every existing consumer.
   */
  ariaLabel?: string;
  /**
   * Story 2-147xd — another real, widely-repeated gap this file's own
   * header comment anticipated growing rather than escaping: story 2-147wg
   * found `pressed` completely unwired (confirmed by reading this file's
   * OWN `ActionControl` at the time — `Button`'s own `pressed`/
   * `aria-pressed` prop existed, this descriptor just never threaded it
   * through), leaving every toggle-button pair/group in the tree unable to
   * use a host (`MyDayPage.tsx`'s mode/group filters, `OrgAccessPage.tsx`'s
   * Free/Pro picker, `OrgFeaturesPage.tsx`'s On/Off + entitlement grids,
   * `PlatformConsolePage.tsx`'s feature-flag state row) — seven-plus
   * independent call sites, well past this codebase's own "rule of three"
   * bar for growing a primitive instead of leaving debt. Forwarded verbatim
   * to `Button`/`IconButton`'s own `pressed` prop (sets `aria-pressed`);
   * omitted (`undefined`) for a non-toggle action, unchanged from before.
   */
  pressed?: boolean;
  /**
   * Story 2-147xd — closes the "no host supports `type=\"submit\" form=\"...\"`"
   * gap `2-147wg` first documented for `CreateInspectionDialog.tsx`'s own
   * Modal-footer submit button (footer lives outside the `<form>` it
   * submits, linked only by the HTML `form` attribute — no RHF/`Form`
   * context involved, so `FormActionDescriptor`'s own RHF-aware `type`
   * below is the wrong tool). Deliberately named DIFFERENTLY from
   * `FormActionDescriptor.type` (which selects `FormSubmit` vs `Button`,
   * an RHF-context concern) — `htmlType`/`form` are the plain native HTML
   * button attributes, forwarded verbatim to `Button`, usable on ANY of
   * the six hosts (a `DialogActions` footer is the real, repeated shape:
   * `OrgAccessPage.tsx` ×2 modals, `PlatformConsolePage.tsx`'s create-org
   * modal, `CreateInspectionDialog.tsx`), not just `FormActions`.
   */
  htmlType?: 'button' | 'submit';
  /** HTML `form` attribute — associates this control with a `<form>` elsewhere in the DOM by id. Paired with `htmlType: 'submit'`; meaningless alone. */
  form?: string;
  /**
   * Story 2-147xd — the product/gallery zero-debt closure's own bespoke-
   * button sweep found a real, repeated (2+ call site) "fill the containing
   * block instead of sizing to content" need that had nothing to do with
   * grouping/overflow — `OAuthProviderButtons.tsx`'s per-provider button and
   * `PaywallModal.tsx`'s per-plan CTA both already reached for `Button`'s own
   * `block` prop (story 2-147c) directly, so this is a straight forward of an
   * EXISTING primitive capability through the descriptor, not new visual
   * vocabulary invented for this story. Forwarded verbatim to `Button`'s own
   * `block`; `IconButton` has no equivalent shape today (no real iconOnly
   * consumer needs it), so this is a no-op on an `iconOnly` action.
   */
  block?: boolean;
  /**
   * Story 2-147xd — forces the control's minimum height onto the touch-
   * target scale (`min-h-touch`, `tailwind.config.ts`'s `--density-touch-
   * target`) instead of the standard fixed control-height token (`size`'s
   * own `h-control`/`h-control-sm`). Real, repeated need named by P05 §9.2's
   * own "sticky navigation never hides controls" / touch-target rule: the
   * mobile capture flow's own controls (`SubmitBar.tsx`'s submit action,
   * `CapturePage.tsx`'s Sections-sheet trigger/Remove-row/Add-a-row
   * controls) all already carried a raw `min-h-touch` class alongside
   * `Button`'s own fixed-height sizing for exactly this reason — CSS's own
   * `min-height` clamps `height` upward when the latter is smaller, so this
   * composes with `size` rather than replacing it (unchanged rendered
   * height from before this field existed). Not merged into `size` itself:
   * `size` picks a CONTROL-HEIGHT SCALE (the same token every other control
   * in the tree consumes), this is an orthogonal "and also never go below
   * the touch-target floor" override a handful of real mobile-capture
   * controls need regardless of which scale step they're on.
   */
  touchTarget?: boolean;
  /**
   * Story 2-147xd — an always-rendered `title` tooltip, independent of
   * `disabled`. Distinct from `disabledReason` (above), which only ever
   * shows once a control is disabled — this is for a control whose VISIBLE
   * label alone doesn't fully explain what clicking it does regardless of
   * state (`PlatformConsolePage.tsx`'s plan-entitlement toggle cell, whose
   * label is only `✓`/`—`; its per-field/per-plan "Revert" buttons, whose
   * label never names which field/plan it reverts). Real, repeated (3
   * independent call sites, all `PlatformConsolePage.tsx`) need this story's
   * own bespoke-button sweep found — not folded into `disabledReason`
   * because that field's whole contract is "only when disabled" and
   * overloading it to also mean "always" would make every EXISTING
   * `disabledReason` consumer ambiguous about which behaviour it gets. Takes
   * priority over `disabledReason` when both are present (a control that
   * explains itself unconditionally has no separate disabled-only sentence
   * to add).
   */
  hint?: string;
  'data-testid'?: string;
}

export interface FormActionDescriptor extends ActionDescriptor {
  /**
   * `'button'` (default): a plain command, rendered via `Button`.
   * `'submit'`: rendered via `FormSubmit` — a real `type="submit"` control
   * inside the enclosing `<Form>`'s own subtree (react-hook-form's
   * `FormProvider` context, exactly like every existing `FormSubmit`
   * consumer). `onClick` is ignored for this type; submission is the
   * surrounding `<Form onSubmit>`'s handler, not this button's own. Always
   * treated as `pinned` regardless of the `pinned` field — a form must
   * never lose its own way to submit to an overflow menu.
   */
  type?: 'button' | 'submit';
  /** `FormSubmit`'s own pending-label override, forwarded verbatim. */
  pendingLabel?: string;
}

type Size = 'sm' | 'md';

function toOverflowMenuAction(action: ActionDescriptor): OverflowMenuAction {
  return {
    key: action.id,
    label: action.label,
    icon: action.icon,
    onSelect: action.onClick ?? (() => {}),
    disabled: Boolean(action.disabled || action.loading),
    destructive: action.variant === 'destructive',
  };
}

/** Shared by all six hosts — see this file's header comment for the algorithm. */
function splitOverflow<T extends ActionDescriptor>(
  actions: T[],
  maxVisible: number,
): { visible: T[]; overflow: T[] } {
  if (actions.length <= maxVisible) return { visible: actions, overflow: [] };
  const pinned = actions.filter((a) => a.pinned);
  const collapsible = actions.filter((a) => !a.pinned);
  const budget = Math.max(maxVisible - pinned.length - 1, 0);
  const inlineCollapsible = collapsible.slice(0, budget);
  const overflow = collapsible.slice(budget);
  const inlineIds = new Set([...pinned, ...inlineCollapsible].map((a) => a.id));
  const visible = actions.filter((a) => inlineIds.has(a.id));
  return { visible, overflow };
}

function ActionControl({ action, size }: { action: ActionDescriptor; size: Size }): JSX.Element {
  const variant = action.variant ?? 'secondary';
  const title = action.hint ?? (action.disabled ? action.disabledReason : undefined);
  const touchClassName = action.touchTarget ? 'min-h-touch' : undefined;
  if (action.iconOnly) {
    return (
      <IconButton
        aria-label={action.label}
        icon={action.icon}
        variant={variant}
        size={size}
        pressed={action.pressed}
        disabled={action.disabled ?? false}
        loading={action.loading ?? false}
        title={title}
        {...(touchClassName !== undefined ? { className: touchClassName } : {})}
        onClick={action.onClick}
        data-testid={action['data-testid']}
      />
    );
  }
  return (
    <Button
      type={action.htmlType ?? 'button'}
      form={action.form}
      variant={variant}
      size={size}
      icon={action.icon}
      pressed={action.pressed}
      disabled={action.disabled ?? false}
      loading={action.loading ?? false}
      block={action.block ?? false}
      title={title}
      aria-label={action.ariaLabel}
      {...(touchClassName !== undefined ? { className: touchClassName } : {})}
      onClick={action.onClick}
      data-testid={action['data-testid']}
    >
      {action.label}
    </Button>
  );
}

function FormActionControl({ action, size }: { action: FormActionDescriptor; size: Size }): JSX.Element {
  if (action.type === 'submit') {
    return (
      <FormSubmit
        variant={action.variant ?? 'primary'}
        size={size}
        disabled={action.disabled ?? false}
        loading={action.loading ?? false}
        {...(action.pendingLabel !== undefined ? { pendingLabel: action.pendingLabel } : {})}
        data-testid={action['data-testid']}
      >
        {action.label}
      </FormSubmit>
    );
  }
  return <ActionControl action={action} size={size} />;
}

interface ClusterProps<T extends ActionDescriptor> {
  actions: T[];
  size: Size;
  maxVisible: number;
  overflowLabel: string;
  overflowAlign: 'left' | 'right';
  render: (action: T, size: Size) => JSX.Element;
}

/** The shared render body every host below configures — never exported directly (see header comment: six DISTINCT hosts, not six copies). */
function ActionCluster<T extends ActionDescriptor>({
  actions,
  size,
  maxVisible,
  overflowLabel,
  overflowAlign,
  render,
}: ClusterProps<T>): JSX.Element | null {
  if (actions.length === 0) return null;
  const { visible, overflow } = splitOverflow(actions, maxVisible);
  return (
    <>
      {visible.map((action) => render(action, size))}
      {overflow.length > 0 && (
        <OverflowMenu
          actions={overflow.map(toOverflowMenuAction)}
          label={overflowLabel}
          align={overflowAlign}
          size={size}
        />
      )}
    </>
  );
}

// ------------------------------------------------------------------
// HeaderActions — the action cluster in a page/section header (right side of
// `PageHeader`'s own `actions` slot, story 2-127; also composable directly
// into any other header-shaped region). Proof-of-concept consumer:
// `pages/OrgAccessPage.tsx`'s "Invite member" / "Convert to organization"
// header actions (see SESSION-HANDOFF.md for why this spot).
// ------------------------------------------------------------------

export interface HeaderActionsProps {
  actions: ActionDescriptor[];
  size?: Size;
  /** Fixed inline budget before the rest collapse into an overflow menu. Header regions are narrow — default keeps room for one primary + a couple of supporting actions. */
  maxVisible?: number;
  overflowLabel?: string;
  className?: string;
  'data-testid'?: string;
}

export function HeaderActions({
  actions,
  size = 'sm',
  maxVisible = 3,
  overflowLabel = 'More actions',
  className = '',
  'data-testid': testId,
}: HeaderActionsProps): JSX.Element | null {
  if (actions.length === 0) return null;
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid={testId}>
      <ActionCluster
        actions={actions}
        size={size}
        maxVisible={maxVisible}
        overflowLabel={overflowLabel}
        overflowAlign="right"
        render={(action, s) => <ActionControl key={action.id} action={action} size={s} />}
      />
    </div>
  );
}

// ------------------------------------------------------------------
// CardActions — actions attached to a card (footer or corner). Proof-of-
// concept consumer: `components/ui/AgentPlanCard.tsx`'s own pending-state
// Approve/Decline footer (`align="start"`, matching that card's existing
// left-aligned layout byte-for-byte). `TemplateCard.tsx`'s ~8-action header
// cluster (finding 4-98) is the REAL example of debt this host exists to
// eventually replace — migrating that file is explicitly out of this
// story's scope (`2-147w*`'s job), see this file's own header comment.
// ------------------------------------------------------------------

export interface CardActionsProps {
  actions: ActionDescriptor[];
  size?: Size;
  /** Card footers read left-to-right like a sentence when `'start'` (e.g. Approve · Decline); `'end'` is the more common "actions pinned to the card's trailing edge" layout. */
  align?: 'start' | 'end';
  maxVisible?: number;
  overflowLabel?: string;
  className?: string;
  'data-testid'?: string;
}

export function CardActions({
  actions,
  size = 'sm',
  align = 'end',
  maxVisible = 3,
  overflowLabel = 'More actions',
  className = '',
  'data-testid': testId,
}: CardActionsProps): JSX.Element | null {
  if (actions.length === 0) return null;
  return (
    <div
      className={`flex flex-wrap items-center gap-2 ${align === 'end' ? 'justify-end' : ''} ${className}`}
      data-testid={testId}
    >
      <ActionCluster
        actions={actions}
        size={size}
        maxVisible={maxVisible}
        overflowLabel={overflowLabel}
        overflowAlign="right"
        render={(action, s) => <ActionControl key={action.id} action={action} size={s} />}
      />
    </div>
  );
}

// ------------------------------------------------------------------
// RowActions — actions in a table/list row. Defaults to a tighter budget
// and compact size than the other hosts — P10 §2.7's own citation for this
// spot ("often icon-only, often overflow into a menu when there are more
// than N"). Proof-of-concept consumer:
// `modules/reports/components/ShareLinksPanel.tsx`'s per-row "Revoke"
// action (`CompactRow`'s `meta` slot).
// ------------------------------------------------------------------

export interface RowActionsProps {
  actions: ActionDescriptor[];
  size?: Size;
  /** A row is the narrowest of the six hosts — 2 inline, everything else overflows. */
  maxVisible?: number;
  overflowLabel?: string;
  className?: string;
  'data-testid'?: string;
}

export function RowActions({
  actions,
  size = 'sm',
  maxVisible = 2,
  overflowLabel = 'Row actions',
  className = '',
  'data-testid': testId,
}: RowActionsProps): JSX.Element | null {
  if (actions.length === 0) return null;
  return (
    <div className={`flex items-center gap-1 ${className}`} data-testid={testId}>
      <ActionCluster
        actions={actions}
        size={size}
        maxVisible={maxVisible}
        overflowLabel={overflowLabel}
        overflowAlign="right"
        render={(action, s) => <ActionControl key={action.id} action={action} size={s} />}
      />
    </div>
  );
}

// ------------------------------------------------------------------
// ToolbarActions — a toolbar strip (e.g. a page's secondary action row —
// `PageHeader`'s own `toolbar` slot, story 2-127). Proof-of-concept
// consumer: `modules/reports/pages/ReportDetailPage.tsx`'s Download PDF /
// Regenerate / Print / Open inspection row — the exact "Report detail...
// toolbar row (Print/Open)" shape `PageHeader.tsx`'s own header comment
// already names. The template editor's Undo/Redo/Save cluster (now
// `2-147wd`) is a real shape this host was designed against but is
// explicitly out of scope to migrate here.
// ------------------------------------------------------------------

export interface ToolbarActionsProps {
  actions: ActionDescriptor[];
  size?: Size;
  maxVisible?: number;
  overflowLabel?: string;
  className?: string;
  'data-testid'?: string;
  /**
   * Story 2-147xd — role/accessible-name passthrough onto this host's own
   * wrapper `div`, for the one real shape that needs it: a `pressed`
   * toggle-button GROUP (`MyDayPage.tsx`'s mode/group filters,
   * `OrgFeaturesPage.tsx`'s On/Off row, `PlatformConsolePage.tsx`'s
   * feature-flag state row) needs `role="group"` + a group-level
   * `aria-label` on its own container — the same real, structural need
   * `Cluster`'s own `role`/`aria-label` passthrough (story 2-147wg) already
   * grew for a non-action grouping (`OAuthProviderButtons.tsx`'s "or"
   * divider). Optional; every existing consumer that omits it is
   * unaffected.
   */
  role?: string;
  'aria-label'?: string;
}

export function ToolbarActions({
  actions,
  size = 'md',
  maxVisible = 4,
  overflowLabel = 'More actions',
  className = '',
  'data-testid': testId,
  role,
  'aria-label': ariaLabel,
}: ToolbarActionsProps): JSX.Element | null {
  if (actions.length === 0) return null;
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid={testId} role={role} aria-label={ariaLabel}>
      <ActionCluster
        actions={actions}
        size={size}
        maxVisible={maxVisible}
        overflowLabel={overflowLabel}
        overflowAlign="right"
        render={(action, s) => <ActionControl key={action.id} action={action} size={s} />}
      />
    </div>
  );
}

// ------------------------------------------------------------------
// FormActions — the Cancel/Submit cluster at the bottom of a form. Renders
// INSIDE the same `<Form>` subtree as its fields (`type: 'submit'` entries
// become real `FormSubmit` controls, reading `formState.isSubmitting` off
// react-hook-form's context) — never as a sibling of `<Form>`, the same
// constraint every existing hand-rolled Cancel/Submit row in this tree
// already documents (`TemplatePublishBar.tsx`, `InspectionLifecycleBar.tsx`:
// "FormProvider context does not extend past this <Form>'s own subtree").
// Proof-of-concept consumer:
// `modules/reports/components/CreateShareLinkDialog.tsx`'s Cancel/"Create
// link" row. Story 2-147ua widened adoption to every real dialog/page form
// that already composed `Form`/`FormField` but was still hand-rolling its
// own `<div className="flex justify-end gap-2">` Cancel/Submit pair
// (`SubjectForm`, `InspectionTypeForm`, `ReassignInspectionDialog`,
// `RequestChangesDialog`) — one governed action row instead of four
// independently-typed copies of the same shape.
//
// Structural crowding guarantee (2-147ua, `flex-wrap` + `justify-end`
// above): this row never needs its own `min-w-0`/`break-words` guard the
// way `Form.tsx`'s `FormField`/`FormError` do — it renders no free text of
// its own, only `Button`/`FormSubmit` labels, and `.btn-glass` (index.css)
// is `white-space: nowrap`, so a button's floor is its own full label
// width. A field/banner ABOVE this row growing taller (wrapping instead of
// overflowing, per `FormField`'s own guarantee) can never translate into
// THIS row's buttons shrinking — the two live in different block boxes,
// stacked vertically by `Form`'s own children, never sharing a flex row.
// ------------------------------------------------------------------

export interface FormActionsProps {
  actions: FormActionDescriptor[];
  size?: Size;
  maxVisible?: number;
  overflowLabel?: string;
  className?: string;
  'data-testid'?: string;
}

export function FormActions({
  actions,
  size = 'md',
  maxVisible = 3,
  overflowLabel = 'More actions',
  className = '',
  'data-testid': testId,
}: FormActionsProps): JSX.Element | null {
  if (actions.length === 0) return null;
  // A form's own submit control can never silently disappear into an
  // overflow menu — pinned regardless of what the caller passed.
  const normalized = actions.map((a) => (a.type === 'submit' ? { ...a, pinned: true } : a));
  return (
    <div className={`flex flex-wrap items-center justify-end gap-2 ${className}`} data-testid={testId}>
      <ActionCluster
        actions={normalized}
        size={size}
        maxVisible={maxVisible}
        overflowLabel={overflowLabel}
        overflowAlign="right"
        render={(action, s) => <FormActionControl key={action.id} action={action} size={s} />}
      />
    </div>
  );
}

// ------------------------------------------------------------------
// DialogActions — the action row in a `Modal` footer. Composes WITH
// `ModalFooter` (story 2-147g) rather than replacing it — `ModalFooter`
// still owns the sticky positioning/hairline-divider chrome, this host only
// supplies the governed button row inside it, matching how `ModalHeader`/
// `ModalBody`/`ModalFooter` are already flat sibling components a consumer
// composes explicitly (see `Modal.tsx`'s own header comment on why —
// "sibling sub-components, not slot props"). Proof-of-concept consumer:
// `modules/reports/components/ShareLinksPanel.tsx`'s `RevokeConfirmDialog`
// Cancel/"Revoke link" footer.
// ------------------------------------------------------------------

export interface DialogActionsProps {
  actions: ActionDescriptor[];
  size?: Size;
  maxVisible?: number;
  overflowLabel?: string;
  className?: string;
  'data-testid'?: string;
}

export function DialogActions({
  actions,
  size = 'md',
  maxVisible = 3,
  overflowLabel = 'More actions',
  className = '',
  'data-testid': testId,
}: DialogActionsProps): JSX.Element | null {
  if (actions.length === 0) return null;
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid={testId}>
      <ActionCluster
        actions={actions}
        size={size}
        maxVisible={maxVisible}
        overflowLabel={overflowLabel}
        overflowAlign="right"
        render={(action, s) => <ActionControl key={action.id} action={action} size={s} />}
      />
    </div>
  );
}

// ------------------------------------------------------------------
// ChoiceGroup — the SEVENTH host, story 2-147xd. A closed, equal-width set of
// mutually-exclusive toggle buttons (`aria-pressed`, exactly one active at a
// time) — a genuinely different SHAPE from the other six, which are all
// `flex flex-wrap` auto-width rows of INDEPENDENT actions. This story's own
// bespoke-button sweep found the same equal-width `<Grid columns={{base:N}}>`
// + raw `<Button aria-pressed>` pair hand-rolled at 3 real, independently-
// evolved call sites — `OrgAccessPage.tsx`'s Convert-to-organization Free/Pro
// picker, `SignupPage.tsx`'s Personal/Organization account-type picker AND
// its own Free/Pro plan picker (2 pickers, one file) — every one already
// composing `Grid` (with `role="group"`/`aria-label` — see `Grid`'s own
// `role` doc, grown for exactly this shape in this same story) around a pair
// of `Button`s that still set `variant`/`aria-pressed` directly. Not folded
// into `ToolbarActions`: that host's whole contract is a `flex flex-wrap`
// auto-width row (P10 §2.7's own "toolbar strip" shape) — an equal-width
// grid cell is a different rendered shape, not a variant of the same one, so
// this earns a genuinely new host rather than an overloaded prop on an
// existing one (the same "grow the vocabulary" discipline this file's own
// header comment states, applied to hosts as much as to descriptor fields).
//
// Deliberately NOT `ActionCluster`/`ActionControl` (no overflow menu, no
// icon-only branch, no `htmlType`/`form`) — a choice group is a closed,
// small, ALWAYS-visible set (2 today, real callers may grow it, but it is
// never a place a "more actions" menu belongs), and every real call site is
// a plain command toggle, never icon-only or a submit control. Each choice
// renders with a fixed `w-full` (the host's own decision, filling its grid
// cell — not a caller-supplied class; see this file's own "no escape hatch"
// section) so the grid cells stay equal-width regardless of label length,
// matching every real call site's own hand-rolled `className="w-full"`
// byte-for-byte.
// ------------------------------------------------------------------

function ChoiceControl({ choice, size }: { choice: ActionDescriptor; size: Size }): JSX.Element {
  return (
    <Button
      type="button"
      variant={choice.variant ?? 'secondary'}
      size={size}
      pressed={choice.pressed}
      disabled={choice.disabled ?? false}
      loading={choice.loading ?? false}
      className="w-full"
      onClick={choice.onClick}
      data-testid={choice['data-testid']}
    >
      {choice.label}
    </Button>
  );
}

export interface ChoiceGroupProps {
  /** Every entry should set `pressed` — a choice group with no active member is a real, valid (if unusual) state, same as any other toggle. */
  choices: ActionDescriptor[];
  size?: Size;
  /** Group-level accessible name — required, not optional: an unlabelled `role="group"` announces nothing to assistive tech (matches `Grid`'s own `role`/`aria-label` pairing contract). */
  'aria-label': string;
  className?: string;
  'data-testid'?: string;
}

export function ChoiceGroup({
  choices,
  size = 'md',
  'aria-label': ariaLabel,
  className = '',
  'data-testid': testId,
}: ChoiceGroupProps): JSX.Element | null {
  if (choices.length === 0) return null;
  // Every real call site today is a 2-up picker; `Grid`'s own `columns`
  // scale (1–6) covers any closed set this shape could plausibly grow to
  // without a new lookup table here.
  const columnCount = Math.min(Math.max(choices.length, 1), 6) as 1 | 2 | 3 | 4 | 5 | 6;
  return (
    <Grid
      columns={{ base: columnCount }}
      gap={2}
      role="group"
      aria-label={ariaLabel}
      className={className}
      {...(testId !== undefined ? { 'data-testid': testId } : {})}
    >
      {choices.map((choice) => (
        <ChoiceControl key={choice.id} choice={choice} size={size} />
      ))}
    </Grid>
  );
}
